import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { webSessionStorage } from '../lib/webSessionStorage';

export type ColumnVisibilityModel = Record<string, boolean>;

interface ColumnPreferencesState {
  preferences: Record<string, ColumnVisibilityModel>;
  getVisibility: (userId: string, tableId: string) => ColumnVisibilityModel | undefined;
  setVisibility: (userId: string, tableId: string, visibility: ColumnVisibilityModel) => void;
  updateColumn: (
    userId: string,
    tableId: string,
    columnKey: string,
    visible: boolean,
  ) => ColumnVisibilityModel;
  clearVisibility: (userId: string, tableId: string) => void;
}

const storageKeyFor = (userId: string, tableId: string) => `${userId}::${tableId}`;

export const useColumnPreferencesStore = create<ColumnPreferencesState>()(
  persist(
    (set, get) => ({
      preferences: {},
      getVisibility: (userId, tableId) => {
        const key = storageKeyFor(userId, tableId);
        return get().preferences[key];
      },
      setVisibility: (userId, tableId, visibility) => {
        const key = storageKeyFor(userId, tableId);
        set((state) => ({
          preferences: {
            ...state.preferences,
            [key]: { ...visibility },
          },
        }));
      },
      updateColumn: (userId, tableId, columnKey, visible) => {
        const key = storageKeyFor(userId, tableId);
        const current = get().preferences[key] ?? {};
        const next = { ...current, [columnKey]: visible };
        set((state) => ({
          preferences: {
            ...state.preferences,
            [key]: next,
          },
        }));
        return next;
      },
      clearVisibility: (userId, tableId) => {
        const key = storageKeyFor(userId, tableId);
        set((state) => {
          if (!(key in state.preferences)) {
            return state;
          }
          const next = { ...state.preferences };
          delete next[key];
          return { preferences: next };
        });
      },
    }),
    {
      name: 'column_preferences_v2',
      storage: webSessionStorage,
      partialize: (state) => ({ preferences: state.preferences }),
    },
  ),
);
